/**
 * Where the header search goes, kept out of the component so it can be tested
 * without rendering a router.
 *
 * Owner rules (2026-11-01, website traveller test):
 * - The search lists only what we sell. Explore-only places stay out.
 * - Enter opens a results page for the words typed. It never jumps to the
 *   first suggestion: "transfer" + Enter used to open Quad Biking in Agafay,
 *   "solo donne" a quad in Rabat.
 * - On a city page the results stay in that city.
 * - Transfers are sold from /transport only (see the API's
 *   transfer-visibility.ts), so the general search cannot list them. A query
 *   that asks for one is matched here against the route list instead.
 */

/** Lower case, accents dropped: "Aéroport" reads as "aeroport". */
export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function tokensOf(query: string): string[] {
  return normalizeSearchText(query).split(/[\s,.;:!?'’"/-]+/).filter(Boolean);
}

/**
 * Words that ask for a ride, in the seven site languages. Matched as stems:
 * a typed word containing one ("flughafentransfer") or the start of one
 * ("transf") counts.
 */
const AIRPORT_STEMS = ["airport", "aeroport", "aeropuerto", "aeroporto", "flughafen", "luchthaven", "مطار"];
const TRANSFER_STEMS = [
  ...AIRPORT_STEMS,
  "transfer", // en, de, nl
  "transfert", // fr
  "traslado", // es
  "trasferimento", // it
  "trasfer",
  "taxi",
  "navette",
  "shuttle",
  "chauffeur",
  "driver",
  "fahrer",
  "conductor",
  "autista",
  "vervoer",
  "نقل",
  "توصيل",
  "سائق",
];

function stemHit(token: string, stems: readonly string[]): boolean {
  return stems.some(
    (stem) => token.includes(stem) || (token.length >= 4 && stem.startsWith(token)),
  );
}

export interface TransferRouteLike {
  slug: string;
  title: string;
}

export interface TransferMatch<R extends TransferRouteLike> {
  /** The query asks for a ride (airport, transfer, taxi, driver …). */
  intent: boolean;
  /** The routes it names, best first; at most two. */
  routes: R[];
  /** One route clearly named by a place ("essaouira transfer"), if any. */
  named: R | null;
}

/**
 * Match a query against the transfer routes.
 *
 * "transfert aéroport" asks for a ride and names no place, so the airport
 * routes lead. "essaouira transfer" names Essaouira, so the Marrakech to
 * Essaouira route is the one. A query with no ride word never matches a route:
 * "essaouira" alone wants the city.
 */
export function matchTransfers<R extends TransferRouteLike>(
  query: string,
  routes: readonly R[],
): TransferMatch<R> {
  const tokens = tokensOf(query);
  const rideTokens = tokens.filter((t) => stemHit(t, TRANSFER_STEMS));
  if (rideTokens.length === 0) return { intent: false, routes: [], named: null };
  const wantsAirport = rideTokens.some((t) => stemHit(t, AIRPORT_STEMS));
  const placeTokens = tokens.filter((t) => !rideTokens.includes(t) && t.length >= 3);

  const scored = routes
    .map((route, index) => {
      const haystack = `${normalizeSearchText(route.title)} ${route.slug.replace(/-/g, " ")}`;
      const placeHits = placeTokens.filter((t) => haystack.includes(t)).length;
      const airport = /airport/.test(route.slug) ? 1 : 0;
      return { route, index, placeHits, airport };
    })
    // Every place word must be in the route, or the route is not what was asked.
    .filter((s) => s.placeHits === placeTokens.length)
    .sort(
      (a, b) =>
        (wantsAirport ? b.airport - a.airport : 0) || a.index - b.index,
    );

  const named = placeTokens.length > 0 && scored.length > 0 ? scored[0].route : null;
  return { intent: true, routes: scored.slice(0, 2).map((s) => s.route), named };
}

type NavTarget =
  | { to: "/$lang/explore"; params: { lang: string }; search: { q: string } }
  | { to: "/$lang/place/$city"; params: { lang: string; city: string }; search: { q: string } }
  | { to: "/$lang/transport"; params: { lang: string }; search: { route?: string } };

/**
 * The page Enter opens.
 *
 * - A ride: the transport hub, with the named route picked when there is one.
 * - On a city page: that city's page filtered by the words, unless the words
 *   name another city ("essaouira quad" typed on the Casablanca page), when
 *   the whole catalogue is searched instead.
 * - Anywhere else: the explore page filtered by the words.
 */
export function searchResultsTarget(input: {
  lang: string;
  query: string;
  /** The city of the page the guest is on, when it is a city or city hub page. */
  citySlug?: string;
  /** The query names a city other than `citySlug`. */
  namesOtherCity?: boolean;
  transfer?: TransferMatch<TransferRouteLike> | null;
}): NavTarget {
  const { lang, query } = input;
  if (input.transfer?.intent) {
    return {
      to: "/$lang/transport",
      params: { lang },
      search: input.transfer.named ? { route: input.transfer.named.slug } : {},
    };
  }
  if (input.citySlug && !input.namesOtherCity) {
    return { to: "/$lang/place/$city", params: { lang, city: input.citySlug }, search: { q: query } };
  }
  return { to: "/$lang/explore", params: { lang }, search: { q: query } };
}

/** Route ids whose `$city` param is the city the guest is browsing. */
export const CITY_PAGE_ROUTE_IDS: ReadonlySet<string> = new Set([
  "/$lang/_layout/place/$city",
  "/$lang/_layout/category/$category/$city",
]);
